import { Modal, Select, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";

import { useDepositCrypto } from "../../../api/currency";
import { ReactComponent as CheckIcon } from "../../../common/images/svg/Check.svg";
import { ReactComponent as CloseIcon } from "../../../common/images/svg/CloseIcon.svg";
import smartLogoDefault from "../../../common/images/svg/smart-logo-icon.svg";
import { ReactComponent as ArrowIcon } from "../../../common/images/svg/SuffixSelectIcon.svg";
import { ButtonOutlinedCustom } from "../../../components/button/buttonOulined";
import { useNotification } from "../../../components/providers/notification/useNotification";
import { useMetadataCurrencyToken } from "../../../utils/hooks/useMetadataCurrencyToken";
import { useMetadataNetwork } from "../../../utils/hooks/useMetadataNetwork";
import useResponsive from "../../../utils/hooks/useResponsive";

const { Option } = Select;

interface ModalDepositProps {
  selectedItem: any;
  setSelectedItem: (item: any) => void;
}

const ModalDeposit = ({ selectedItem, setSelectedItem }: ModalDepositProps) => {
  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [selectedNetwork, setSelectedNetwork] = useState<any>();
  const [copied, setCopied] = useState(false);
  const [addressDeposit, setAddresDeposit] = useState("");
  const [addressInvalid, setAddressInvalid] = useState(false);
  const [minimumDeposit, setMinimumDeposit] = useState("0");

  const { dataCurrencyToken } = useMetadataCurrencyToken();
  const { dataNetwork } = useMetadataNetwork(Number(selectedCurrency || 0));

  const { mutate: getAddressDeposit, isLoading } = useDepositCrypto({
    onSuccess: (response: {
      data: {
        data: { address: string; assetConfig: { minimumDeposit: string } };
      };
    }) => {
      setAddresDeposit(response.data.data.address);
      setMinimumDeposit(response.data.data.assetConfig.minimumDeposit);
    },
    onError: () => {
      setAddressInvalid(true);
    },
  });

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(addressDeposit);
    setCopied(true);
    if (openNotification) {
      openNotification?.({
        key: "add_bank_success",
        message: "Copied Deposit Address successfully!",
        type: "success",
      });
    }
  };

  const handleCancel = () => {
    navigate("/home");
    setSelectedItem(null);
    setSelectedCurrency(null);
    setSelectedNetwork(null);
    setAddresDeposit("");
    setAddressInvalid(false);
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  useEffect(() => {
    if (!selectedItem) return;
    setSelectedCurrency(selectedItem?.id);
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedCurrency || !selectedNetwork) return;
    getAddressDeposit({
      assetConfigGroupId: selectedCurrency,
      networkId: selectedNetwork,
    });
  }, [selectedCurrency, selectedNetwork]);

  return (
    <Modal
      open={!!selectedItem}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
      title={
        <>
          <Typography
            style={{
              fontSize: isTablet ? 16 : 20,
              fontWeight: 700,
              color: "#C2C6CE",
            }}
          >
            {"Deposit"}
          </Typography>
          <CloseIcon onClick={handleCancel} className="icon-animation" />
        </>
      }
      className="deposit-modal"
    >
      <div className="modal-deposit">
        <div className="modal-deposit-step">
          <div className="modal-deposit-step-header">
            <div className="step-button">
              <Typography
                style={{
                  fontSize: isTablet ? 12 : 16,
                  fontWeight: 700,
                  color: "white",
                }}
              >
                {1}
              </Typography>
            </div>
            <Typography className="step-text">{"Currency"}</Typography>
          </div>
          <div className="modal-deposit-step-content">
            <Select
              suffixIcon={<ArrowIcon />}
              popupClassName="custom-select-dropdown"
              onChange={(value) => {
                setSelectedCurrency(value);
                setAddresDeposit("");
                setMinimumDeposit("0");
                setSelectedNetwork(null);
              }}
              className="select-custom-deposit"
              value={selectedCurrency}
            >
              {Array.isArray(dataCurrencyToken) &&
                dataCurrencyToken.map((item: any) => (
                  <Option key={item?.id} value={item.id}>
                    <div className="currency-option">
                      <img
                        src={
                          `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                        }
                        onError={(e) => {
                          e.currentTarget.src = smartLogoDefault;
                        }}
                        style={{
                          objectFit: "cover",
                          width: isTablet ? 24 : 32,
                          height: isTablet ? 24 : 32,
                          borderRadius: "50%",
                        }}
                        alt="currency"
                      />
                      <span>{item.symbol}</span>
                    </div>
                    {selectedCurrency === item.id && <CheckIcon />}
                  </Option>
                ))}
            </Select>
          </div>
        </div>
        <div className="modal-deposit-step">
          <div className="modal-deposit-step-header">
            <div className="step-button">
              <Typography
                style={{
                  fontSize: isTablet ? 12 : 16,
                  fontWeight: 700,
                  color: "white",
                }}
              >
                {2}
              </Typography>
            </div>
            <Typography className="step-text">{"Network"}</Typography>
          </div>
          <div className="modal-deposit-step-content">
            <Select
              placeholder="Choose a network"
              suffixIcon={<ArrowIcon />}
              popupClassName="custom-select-dropdown"
              onChange={(value) => {
                setSelectedNetwork(value);
                setAddresDeposit("");
                setMinimumDeposit("0");
              }}
              value={selectedNetwork}
              className="select-custom-deposit"
            >
              {Array.isArray(dataNetwork) &&
                dataNetwork.map((item: any) => (
                  <Option key={item?.id} value={item.id}>
                    <div className="currency-option">
                      <span>{item.name}</span>
                    </div>
                    {selectedNetwork === item.id && <CheckIcon />}
                  </Option>
                ))}
            </Select>
          </div>
        </div>
        <div className="modal-deposit-step">
          <div
            className="modal-deposit-step-header"
            style={{ opacity: selectedNetwork ? 1 : 0.4 }}
          >
            <div className="step-button">
              <Typography
                style={{
                  fontSize: isTablet ? 12 : 16,
                  fontWeight: 700,
                  color: "white",
                }}
              >
                {3}
              </Typography>
            </div>
            <Typography className="step-text">{"Deposit Address"}</Typography>
          </div>
          {selectedNetwork ? (
            <div className="modal-deposit-step-qr">
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Spin size="large" className="custom-spin" />
                </div>
              ) : (
                <>
                  <div
                    className={`modal-deposit-step-qr-item ${addressDeposit ? "" : "qr-disabled"
                      }`}
                  >
                    {addressDeposit ? (
                      <QRCode
                        size={256}
                        style={{
                          height: "auto",
                          maxWidth: "100%",
                          width: "100%",
                        }}
                        value={addressDeposit}
                        viewBox={`0 0 256 256`}
                      />
                    ) : null}
                  </div>
                  <div className="modal-deposit-step-qr-address">
                    <Typography
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        color: addressDeposit
                          ? "white"
                          : addressInvalid
                            ? "rgba(236, 51, 51, 1)"
                            : "rgba(255, 255, 255, 0.35)",
                      }}
                    >
                      {addressDeposit
                        ? addressDeposit
                        : addressInvalid
                          ? "Unable to generate Deposit Address. Please reselect the currency or network!"
                          : "e.g. 0xxxx…"}
                    </Typography>
                    <ButtonOutlinedCustom
                      size={isTablet ? "small" : "medium"}
                      text={copied ? "Copied" : "Copy to Clipboard"}
                      width={"100%"}
                      onClick={handleCopy}
                      disabled={copied || !addressDeposit}
                    />
                    <Typography
                      style={{
                        fontSize: isTablet ? 10 : 12,
                        color: "#F28A28",
                      }}
                    >
                      {addressInvalid
                        ? ""
                        : `Minimum deposit: ${minimumDeposit} ${(
                          dataCurrencyToken.find(
                            (item: any) => item.id === selectedCurrency
                          ) as any
                        )?.symbol}`}
                    </Typography>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default ModalDeposit;
